import React, { useState, useCallback, createContext, useContext, useRef } from 'react';
import './Toast.css';

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

let toastCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastsRef = useRef([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastCounter;
    setToasts(prev => {
      const next = [...prev, { id, message, type }];
      toastsRef.current = next;
      return next;
    });
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => {
          const next = prev.filter(t => t.id !== id);
          toastsRef.current = next;
          return next;
        });
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => {
      const next = prev.filter(t => t.id !== id);
      toastsRef.current = next;
      return next;
    });
  }, []);

  const success = useCallback((msg) => addToast(msg, 'success'), [addToast]);
  const error = useCallback((msg) => addToast(msg, 'error', 6000), [addToast]);
  const info = useCallback((msg) => addToast(msg, 'info'), [addToast]);
  const warning = useCallback((msg) => addToast(msg, 'warning'), [addToast]);

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      const id = ++toastCounter;
      const timeout = setTimeout(() => {
        resolve(false);
        setToasts(prev => {
          const next = prev.filter(t => t.id !== id);
          toastsRef.current = next;
          return next;
        });
      }, 30000);
      setToasts(prev => {
        const next = [...prev, { id, message, type: 'confirm', resolve, timeout }];
        toastsRef.current = next;
        return next;
      });
    });
  }, []);

  const handleConfirm = useCallback((id, result) => {
    setToasts(prev => {
      const toast = prev.find(t => t.id === id);
      if (toast?.resolve) toast.resolve(result);
      if (toast?.timeout) clearTimeout(toast.timeout);
      const next = prev.filter(t => t.id !== id);
      toastsRef.current = next;
      return next;
    });
  }, []);

  const confirmToasts = toasts.filter(t => t.type === 'confirm');
  const regularToasts = toasts.filter(t => t.type !== 'confirm');

  return (
    <ToastContext.Provider value={{ success, error, info, warning, confirm }}>
      {children}
      <div className="toast-container" aria-live="polite" aria-atomic="true">
        {regularToasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`} role={toast.type === 'error' ? 'alert' : 'status'} onClick={() => removeToast(toast.id)}>
            <span className="toast-message">{toast.message}</span>
          </div>
        ))}
      </div>
      {confirmToasts.map(toast => (
        <div key={toast.id} className="toast-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleConfirm(toast.id, false); }}>
          <div className="toast-confirm-modal" role="alertdialog" aria-modal="true">
            <p className="toast-message">{toast.message}</p>
            <div className="toast-confirm-buttons">
              <button className="toast-btn toast-btn-cancel" onClick={() => handleConfirm(toast.id, false)}>Cancel</button>
              <button className="toast-btn toast-btn-ok" onClick={() => handleConfirm(toast.id, true)}>Delete</button>
            </div>
          </div>
        </div>
      ))}
    </ToastContext.Provider>
  );
};
